import Image from 'next/image'

import clsx from 'clsx'

import { AppFragment } from '@/generated/contentful'

import { shouldAppReviewTemporarilyHidden } from '@/utils/apps/constants'

import { Link } from '@/components/CustomLink'

import { ReviewStars } from '../AppCard/ReviewStars'
import featuredIcon from './icon-star.svg'

type Props = Pick<
	AppFragment,
	| 'logo'
	| 'name'
	| 'slug'
	| 'reviewCount'
	| 'upfrontPrice'
	| 'monthlyPrice'
	| 'rating'
	| 'pricingType'
> & {
	tier: {
		logo?: string | null
		title?: string | null
	} | null
	featured?: boolean | null
	summary?: string | null
	categories?: Array<{
		slug?: string | null
		name?: string | null
	}>
}

export function AppFeedCard({
	logo,
	name,
	slug,
	featured,
	summary,
	categories,
	reviewCount,
	upfrontPrice,
	monthlyPrice,
	pricingType,
	rating,
	tier
}: Props) {
	return (
		<div
			data-testid="app-card"
			className={`min-w-[250px] lg:min-w-[200px] lg:min-w-auto bg-white flex flex-col shadow-lg rounded-xl relative px-6 pb-6 h-full`}
		>
			<Link href={`/apps/${slug}`} className="absolute inset-0 z-10" />
			{featured && (
				<Image className="absolute top-0 right-0" src={featuredIcon} alt="featuredIcon" />
			)}
			{logo && (
				<div className="relative w-full h-[158px]">
					<Image
						src={logo.startsWith('//') ? `https:${logo}` : logo}
						alt={name || ''}
						fill
						className="object-contain"
					/>
				</div>
			)}

			{name && <h3 className="mt-3 text-bc-black font-bold">{name}</h3>}

			{categories && categories?.length > 0 && (
				<div className="flex flex-wrap my-2 gap-y-1">
					{categories.map((category, i, arr) => {
						if (!category || !category.name || !category.slug) return null

						return (
							<span className="text-bc-blue font-medium relative z-[11]" key={i}>
								<Link href={`/apps/categories/${category.slug}/`}>{category.name}</Link>
								{i < arr.length - 1 && <>,&nbsp;</>}
							</span>
						)
					})}
				</div>
			)}

			{summary && <p className="text-gray-700 line-clamp-3">{summary}</p>}

			<h4 className="my-3 text-bc-black font-medium">Pricing</h4>

			<ul className="text-gray-700 line-clamp-3 mb-6">
				{pricingType === 'Other' ? (
					<li>Custom Pricing</li>
				) : (
					<>
						<li>
							Upfront fee:
							{upfrontPrice
								? ` $${new Intl.NumberFormat('en-US').format(parseInt(upfrontPrice))}`
								: ' Free'}
						</li>

						<li>
							Recurring fee:
							{monthlyPrice
								? ` $${new Intl.NumberFormat('en-US').format(parseInt(monthlyPrice))}`
								: ' None'}
						</li>
					</>
				)}
			</ul>

			<div
				className={clsx(
					'flex mt-auto items-end',
					!shouldAppReviewTemporarilyHidden(name) ? 'justify-between ' : ' justify-end'
				)}
			>
				{!shouldAppReviewTemporarilyHidden(name) && (
					<div>
						{typeof rating === 'number' ? (
							<ReviewStars rating={rating} />
						) : (
							<p className="text-xs text-gray-500">No reviews</p>
						)}

						{typeof reviewCount === 'number' && reviewCount > 0 && (
							<span className="mt-1 block text-xs text-gray-500">
								{reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
							</span>
						)}
					</div>
				)}

				{tier?.logo && (
					<Image
						src={tier.logo}
						alt={tier.title || ''}
						width={24}
						height={24}
						className="object-contain"
					/>
				)}
			</div>
		</div>
	)
}
